import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Send, Sparkles, Loader2, RefreshCw, Share2, UploadCloud, Monitor, FolderTree,
  ChevronDown, Plus, Bot, CheckCircle2, XCircle, FileCode2, ExternalLink, Home,
  Paperclip, X, Coins, Globe, Smartphone, Mic, GitFork, Github, Cpu, SlidersHorizontal,
  FilePen, FilePlus, FileText, Terminal, List, Database, RotateCw, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { api, BACKEND_URL, fileUrl } from '@/lib/api';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { LuxeButton, Spinner, Empty, Input } from '@/components/bits';
import { ManageTabs } from '@/components/ManagePanels';

// A full, Emergent-style completion report built from the agent's final output + steps.
function BuildReport({ final, steps }) {
  if (!final) return null;
  const seen = {};
  (steps || []).forEach((s) => {
    if ((s.tool === 'write_file' || s.tool === 'apply_edit') && !s.error && s.path) {
      seen[s.path] = { path: s.path, created: s.created !== false };
    }
  });
  const files = Object.values(seen);
  const benefits = final.benefits || [];
  const risks = final.risks || [];
  const blocked = final.blocked;
  return (
    <div className="mt-3 rounded-xl overflow-hidden" data-testid="build-report" style={{ border: `1px solid ${luxe.border}` }}>
      <div className="px-3 py-2 flex items-center gap-2" style={{ background: blocked ? 'rgba(252,211,77,0.12)' : 'rgba(16,185,129,0.12)', borderBottom: `1px solid ${luxe.border}` }}>
        {blocked ? <AlertTriangle size={15} style={{ color: '#FCD34D' }} /> : <CheckCircle2 size={15} style={{ color: luxe.success }} />}
        <span className="text-xs font-bold" style={{ color: blocked ? '#FCD34D' : luxe.success }}>{blocked ? 'Build needs input' : 'Build report'}</span>
      </div>
      <div className="p-3 space-y-3" style={{ background: luxe.bgElev }}>
        {final.summary && <div className="text-sm leading-relaxed" style={{ color: luxe.text }}>{final.summary}</div>}
        {final.needs && <div className="text-xs" style={{ color: '#FCD34D' }}>Needs: {final.needs}</div>}
        {files.length > 0 && (
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: luxe.muted }}>Files ({files.length})</div>
            <div className="space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {f.created ? <FilePlus size={12} style={{ color: luxe.textMid, flexShrink: 0 }} /> : <FilePen size={12} style={{ color: luxe.textMid, flexShrink: 0 }} />}
                  <span className="font-mono truncate" style={{ color: '#93C5FD' }}>{f.path}</span>
                  <span className="text-[10px]" style={{ color: luxe.muted, flexShrink: 0 }}>{f.created ? 'created' : 'edited'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {benefits.length > 0 && (
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: luxe.muted }}>What works</div>
            <div className="space-y-1">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-xs" style={{ color: luxe.textMid }}><CheckCircle2 size={12} style={{ color: luxe.success, marginTop: 2, flexShrink: 0 }} /><span>{b}</span></div>
              ))}
            </div>
          </div>
        )}
        {risks.length > 0 && (
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: luxe.muted }}>Check / notes</div>
            <div className="space-y-1">
              {risks.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-xs" style={{ color: luxe.textMid }}><AlertTriangle size={12} style={{ color: '#FCD34D', marginTop: 2, flexShrink: 0 }} /><span>{r}</span></div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const P = PILLARS.coding;
const RUNNING = ['awaiting_approval', 'running'];

// Turn a raw agent tool-step into a clean, Emergent-style row (verb + file/command),
// hiding raw file content. Falls back to parsing args_preview for older records.
function prettyStep(s) {
  const tool = s.tool || '';
  let path = s.path || '';
  let cmd = s.cmd || '';
  if (!path && !cmd && s.args_preview) {
    try { const a = JSON.parse(s.args_preview); path = a.path || ''; cmd = a.cmd || ''; } catch { /* ignore */ }
  }
  switch (tool) {
    case 'write_file': return { Icon: s.created === false ? FilePen : FilePlus, verb: s.created === false ? 'Edited' : 'Created', detail: path, mono: true };
    case 'apply_edit': return { Icon: FilePen, verb: 'Edited', detail: path, mono: true };
    case 'read_file': return { Icon: FileText, verb: 'Read', detail: path, mono: true };
    case 'read_log': return { Icon: FileText, verb: 'Read log', detail: path, mono: true };
    case 'list_files': return { Icon: List, verb: 'Listed', detail: path || 'files', mono: true };
    case 'run_bash': return { Icon: Terminal, verb: '', detail: cmd, mono: true, term: true };
    case 'restart_service': return { Icon: RotateCw, verb: 'Restarted', detail: path || 'service', mono: false };
    case 'db_query': return { Icon: Database, verb: 'Queried database', detail: '', mono: false };
    default: return { Icon: FileCode2, verb: tool, detail: path || cmd, mono: true };
  }
}

// One agent step, Emergent-style: optional narration on top + an expandable file/command pill.
function StepRow({ s }) {
  const [open, setOpen] = useState(false);
  const meta = prettyStep(s);
  const Icon = meta.Icon;
  const detailText = s.error || s.result_preview || '';
  const canExpand = !!detailText;
  return (
    <div>
      {s.thought && <div className="text-sm leading-relaxed mb-1.5" style={{ color: luxe.text }}>{s.thought}</div>}
      <button
        type="button"
        onClick={() => canExpand && setOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-xs px-2.5 py-2 rounded-lg transition-colors"
        style={{ background: luxe.bgElev, border: `1px solid ${luxe.border}`, cursor: canExpand ? 'pointer' : 'default' }}
      >
        <Icon size={14} style={{ color: s.error ? luxe.danger : luxe.textMid, flexShrink: 0 }} />
        <div className="flex-1 min-w-0 flex items-center gap-2 text-left">
          {meta.term ? (
            <span className="font-mono truncate" style={{ color: luxe.textMid }}><span style={{ color: luxe.muted }}>$</span> {meta.detail}</span>
          ) : (
            <>
              {meta.verb && <span className="font-semibold" style={{ color: s.error ? luxe.danger : luxe.text, flexShrink: 0 }}>{meta.verb}</span>}
              {meta.detail && <span className={meta.mono ? 'font-mono truncate' : 'truncate'} style={{ color: '#93C5FD' }}>{meta.detail}</span>}
            </>
          )}
        </div>
        {s.error && <span className="text-[10px] font-mono" style={{ color: luxe.danger, flexShrink: 0 }}>failed</span>}
        {canExpand && <ChevronRight size={15} style={{ color: luxe.muted, flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />}
      </button>
      {open && detailText && (
        <div className="mt-1 px-3 py-2 rounded-lg text-[11px] font-mono whitespace-pre-wrap break-words" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: s.error ? luxe.danger : luxe.muted, maxHeight: 220, overflow: 'auto' }}>
          {detailText}
        </div>
      )}
    </div>
  );
}

function useProjects() {
  const [projects, setProjects] = useState([]);
  const refresh = useCallback(async () => {
    const pr = await api('/admin/agents/projects').catch(() => []);
    const list = (Array.isArray(pr) ? pr : pr.items || []).filter((p) => p.id !== 'cambri');
    setProjects(list);
    return list;
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { projects, setProjects, refresh };
}

export default function AppBuilder() {
  const navigate = useNavigate();
  const { projects, refresh } = useProjects();
  const [projectId, setProjectId] = useState('');
  const [projName, setProjName] = useState('');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('preview');
  const [files, setFiles] = useState([]);
  const [hasIndex, setHasIndex] = useState(false);
  const [openFile, setOpenFile] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickOpen, setPickOpen] = useState(false);
  const [mode, setMode] = useState('app');
  const [attach, setAttach] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cred, setCred] = useState({ balance: null, percentage: 100, low: false });
  const [providers, setProviders] = useState([]);
  const [model, setModel] = useState(null);       // {provider, model, label}
  const [modelOpen, setModelOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);   // false | 'connectors' | ...
  const [gh, setGh] = useState(null);   // {status, repo, branch, message, changes} | null=closed
  const [ghToken, setGhToken] = useState({ username: '', token: '' });
  const [clarify, setClarify] = useState(null);   // {questions, answers, prompt, att} | null
  const [cgpt, setCgpt] = useState(null);   // {open, loading, connected} | null=closed
  const [cgptKey, setCgptKey] = useState('');
  const fileRef = useRef(null);
  const chatRef = useRef(null);
  const pollRef = useRef(null);
  const recRef = useRef(null);

  useEffect(() => { api('/admin/agents/llm/providers').then((r) => setProviders(r.providers || [])).catch(() => {}); }, []);

  useEffect(() => { api('/studio-credits/balance').then(setCred).catch(() => {}); }, [busy]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);
  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [messages, busy]);

  const loadFiles = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const r = await api(`/admin/agents/projects/${pid}/files`);
      setFiles(r.items || []); setHasIndex(!!r.has_index);
    } catch { setFiles([]); setHasIndex(false); }
  }, []);
  useEffect(() => { if (projectId) loadFiles(projectId); }, [projectId, loadFiles]);

  // Restore a project's full coding chat (A→Z) from its saved tasks — like Emergent.
  const loadHistory = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const tasks = await api(`/admin/agents/tasks?project=${encodeURIComponent(pid)}&limit=50`);
      const msgs = [];
      (tasks || []).forEach((t) => {
        const clean = (t.prompt || '')
          .replace(/^Build a (app|website):\s*/i, '')
          .replace(/^Update the existing (app|website)[^:]*:\s*/i, '')
          .split('\n\n[')[0];
        msgs.push({ role: 'user', text: clean });
        msgs.push({ role: 'agent', agent: 'MyRaai Agents', text: t.plan?.summary || '', steps: t.steps || [], status: t.status, final: t.final || null });
      });
      setMessages(msgs);
    } catch { setMessages([]); }
  }, []);

  const currentProject = projects.find((p) => p.id === projectId);
  const previewUrl = projectId ? `${BACKEND_URL}/api/preview/${projectId}/?t=${refreshKey}` : '';

  const ensureProject = async (firstPrompt) => {
    if (projectId) return projectId;
    const name = (projName || firstPrompt.slice(0, 28) || 'My App').trim();
    const res = await api('/admin/agents/projects/create', { method: 'POST', body: { name } });
    await refresh();
    const pid = res.id || res.project?.id;
    setProjectId(pid);
    return pid;
  };

  const appendToLast = (patch) => setMessages((m) => {
    const copy = [...m];
    const last = copy[copy.length - 1];
    copy[copy.length - 1] = { ...last, ...patch(last) };
    return copy;
  });

  const pollTask = (taskId, pid) => {
    pollRef.current = setInterval(async () => {
      try {
        const t = await api(`/admin/agents/task/${taskId}`);
        appendToLast((last) => ({ steps: t.steps || last.steps, status: t.status, final: t.final || last.final, calls: t.calls_used }));
        if (!RUNNING.includes(t.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          setBusy(false);
          const built = pid || projectId;
          if (built) setProjectId(built);   // auto-select the freshly built project
          setRefreshKey((k) => k + 1);
          await loadFiles(built);
          if (t.status === 'completed') { setTab('preview'); toast.success('Build complete — preview opened'); }
          else toast.error(`Build ${t.status}`);
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Please pick an image');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api('/upload/image', { method: 'POST', body: fd, isForm: true });
      setAttach({ path: r.path, url: r.url });
      toast.success('Reference image attached');
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const runBuild = async (prompt, pushUserMsg) => {
    if (pushUserMsg) setMessages((m) => [...m, pushUserMsg]);
    setMessages((m) => [...m, { role: 'agent', text: 'Planning your build…', agent: 'MyRaai Agents', steps: [], status: 'running', final: null }]);
    setBusy(true);
    try {
      const pid = await ensureProject(prompt);
      const body = { agent_id: 'coding_agent', prompt, budget: 'medium', project: pid };
      if (model?.provider && model?.model) { body.preferred_provider = model.provider; body.preferred_model = model.model; }
      const started = await api('/admin/agents/task/start', { method: 'POST', body });
      appendToLast((last) => ({ text: started.plan?.summary || 'Building…', plan: started.plan }));
      await api(`/admin/agents/task/${started.id}/approve`, { method: 'POST' });
      appendToLast(() => ({ status: 'running' }));
      pollTask(started.id, pid);
    } catch (e) {
      appendToLast(() => ({ status: 'failed', text: e.message }));
      setBusy(false); toast.error(e.message);
    }
  };

  const send = async () => {
    if (busy) return;
    if (text.trim().length < 3) return toast.error('Describe what to build');
    const kind = mode === 'website' ? 'website' : 'app';
    let prompt = text.trim();
    const rawText = text.trim();
    const att = attach;
    const userText = prompt + (att ? '  📎 (reference image attached)' : '');
    if (att) prompt += `\n\n[Design reference image attached at ${BACKEND_URL}${att.url} — match its visual style, colours and layout as closely as possible.]`;
    const isFollowUp = !!projectId && files.length > 0;
    prompt = isFollowUp
      ? `Update the existing ${kind} (modify the current code, do NOT rebuild from scratch): ${prompt}`
      : `Build a ${kind}: ${prompt}`;
    setText('');
    setAttach(null);
    const userMsg = { role: 'user', text: userText, image_url: att?.url };
    setBusy(true);
    // Myra co-pilot: ask smart clarifying questions before building (self-gates for clear requests).
    try {
      const body = { prompt: `Build a ${kind}: ${rawText}` };
      if (model?.provider && model?.model) { body.preferred_provider = model.provider; body.preferred_model = model.model; }
      const c = await api('/admin/agents/clarify', { method: 'POST', body });
      if (c.questions && c.questions.length) {
        setMessages((m) => [...m, userMsg]);
        setBusy(false);
        setClarify({ questions: c.questions, answers: {}, prompt, att });
        return;
      }
    } catch { /* clarify optional — fall through to build */ }
    setBusy(false);
    await runBuild(prompt, userMsg);
  };

  const submitClarify = async (skip) => {
    if (!clarify) return;
    const { questions, answers, prompt } = clarify;
    let finalPrompt = prompt;
    if (!skip) {
      const lines = questions.map((q, i) => (answers[i] && answers[i].trim()) ? `- ${q.q} → ${answers[i].trim()}` : null).filter(Boolean);
      if (lines.length) finalPrompt = `${prompt}\n\n[The user answered these preferences — follow them exactly]\n${lines.join('\n')}`;
    }
    setClarify(null);
    await runBuild(finalPrompt, null);
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error('Voice input is not supported in this browser');
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = 'en-US'; rec.interimResults = false; rec.continuous = false;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setText((p) => (p ? p + ' ' : '') + t); };
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error('Voice capture failed — please try again'); };
    recRef.current = rec; setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };

  const forkWorkspace = async () => {
    if (!projectId) return toast.error('Open or create a project first');
    const name = window.prompt('Fork name (independent copy — secrets, .env & .git are NOT copied):');
    if (!name || !name.trim()) return;
    try {
      const r = await api('/admin/agents/projects/fork', { method: 'POST', body: { source_project: projectId, name: name.trim() } });
      await refresh();
      toast.success(`Forked → ${r.id} · ${r.memory_entries_copied} memories copied · secrets excluded`);
    } catch (e) { toast.error(e.message); }
  };

  const openChatGPT = async () => {
    setCgpt({ open: true, loading: true });
    try { const s = await api('/coding/chatgpt/status'); setCgpt({ open: true, loading: false, connected: s.connected }); }
    catch { setCgpt({ open: true, loading: false, connected: false }); }
  };
  const cgptConnect = async () => {
    if (cgptKey.trim().length < 20) return toast.error('Apni OpenAI API key paste karein');
    try {
      await api('/coding/chatgpt/connect', { method: 'POST', body: { api_key: cgptKey.trim() } });
      toast.success('ChatGPT connected ✓ — ab aapki builds aapki hi ChatGPT se chalengi');
      setCgptKey(''); setCgpt({ open: true, loading: false, connected: true });
    } catch (e) { toast.error(e.message); }
  };
  const cgptDisconnect = async () => {
    try { await api('/coding/chatgpt/disconnect', { method: 'DELETE' }); setCgpt({ open: true, loading: false, connected: false }); toast.success('Disconnected — builds ab platform key par'); }
    catch (e) { toast.error(e.message); }
  };

  const openGithub = async () => {
    setGh({ loading: true });
    try {
      const s = await api('/coding/github/status');
      setGh({ status: s, repo: '', branch: 'main', message: '', changes: null });
    } catch (e) { toast.error(e.message); setGh(null); }
  };
  const ghConnect = async () => {
    if (!ghToken.username.trim() || ghToken.token.trim().length < 10) return toast.error('Enter your GitHub username and a valid token');
    try {
      const r = await api('/coding/github/connect', { method: 'POST', body: ghToken });
      toast.success(`GitHub connected as ${r.account}`); setGhToken({ username: '', token: '' });
      const s = await api('/coding/github/status'); setGh((g) => ({ ...g, status: s }));
    } catch (e) { toast.error(e.message); }
  };
  const ghDisconnect = async () => {
    try { await api('/coding/github/disconnect', { method: 'POST' }); const s = await api('/coding/github/status'); setGh((g) => ({ ...g, status: s })); toast.success('Disconnected'); }
    catch (e) { toast.error(e.message); }
  };
  const ghReview = async () => {
    if (!projectId) return toast.error('Open a project first');
    try { const c = await api(`/coding/github/changes/${projectId}`); setGh((g) => ({ ...g, changes: c })); }
    catch (e) { toast.error(e.message); }
  };
  const ghPush = async () => {
    if (!projectId) return toast.error('Open a project first');
    if (!gh.repo.trim() || !gh.message.trim()) return toast.error('Enter repository and commit message');
    try {
      await api('/coding/github/push', { method: 'POST', body: { project: projectId, repo: gh.repo.trim(), branch: gh.branch.trim() || 'main', message: gh.message.trim() } });
      toast.success('Pushed to GitHub');
    } catch (e) { toast.error(e.message); }
  };

  const openFileContent = async (path) => {
    try { const r = await api(`/admin/agents/projects/${projectId}/file?path=${encodeURIComponent(path)}`); setOpenFile({ path, content: r.content, buffer: r.content }); }
    catch (e) { toast.error(e.message); }
  };
  const saveFile = async () => {
    if (!openFile) return;
    try {
      await api(`/admin/agents/projects/${projectId}/file`, { method: 'PUT', body: { path: openFile.path, content: openFile.buffer } });
      setOpenFile({ ...openFile, content: openFile.buffer });
      toast.success(`Saved ${openFile.path}`);
    } catch (e) {
      const d = e && e.data && e.data.detail && e.data.detail.error;
      if (d === 'SECRET_DETECTED') toast.error('Save blocked: potential secret detected in file');
      else toast.error((e && e.message) || 'Save failed');
    }
  };

  const shareLink = () => `${window.location.origin}/live/${projectId}`;
  const share = () => { navigator.clipboard.writeText(shareLink()); toast.success('Share link copied — opens a clean public page!'); };
  const publish = async () => {
    if (!projectId) return toast.error('Build something first');
    if (!hasIndex) return toast.error('No web app to publish yet — build a website first');
    try {
      await api(`/admin/agents/projects/${projectId}/publish`, { method: 'POST', body: { published: true } });
      navigator.clipboard.writeText(shareLink());
      toast.success('Published! Clean share link copied 🚀');
      window.open(shareLink(), '_blank');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 58px)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 flex-wrap" style={{ background: luxe.bgElev, borderBottom: `1px solid ${luxe.border}` }}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: gradientCss(P.grad) }}><FileCode2 size={14} color="#fff" /></div>
            <span className="text-sm font-bold" style={{ color: luxe.text }}>Project</span>
          </div>
          {/* App / Website mode toggle */}
          <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${luxe.border}` }}>
            {[['app', 'App Build', Smartphone], ['website', 'Website Build', Globe]].map(([id, label, Icon]) => (
              <button key={id} onClick={() => setMode(id)} data-testid={`build-mode-${id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold" style={{ background: mode === id ? gradientCss(P.grad) : 'transparent', color: mode === id ? '#fff' : luxe.muted }}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          {/* project picker */}
          <div className="relative">
            <button onClick={() => setPickOpen(!pickOpen)} data-testid="project-picker" className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: currentProject ? luxe.text : luxe.muted }}>
              <span className="w-2 h-2 rounded-full" style={{ background: currentProject ? luxe.success : luxe.muted }} />
              {currentProject?.name || 'New project'} <ChevronDown size={14} />
            </button>
            {pickOpen && (
              <div className="absolute z-40 mt-1 w-64 p-2 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.borderHi}`, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
                <div className="flex gap-1 mb-2">
                  <Input value={projName} onChange={(e) => setProjName(e.target.value)} placeholder="New project name" style={{ padding: '8px 10px' }} data-testid="new-project-name" />
                  <button onClick={async () => { if (!projName.trim()) return; const r = await api('/admin/agents/projects/create', { method: 'POST', body: { name: projName.trim() } }); await refresh(); setProjectId(r.id); setProjName(''); setPickOpen(false); setMessages([]); }} className="px-2 rounded-lg" style={{ background: gradientCss(P.grad), color: '#fff' }}><Plus size={16} /></button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {projects.map((p) => (
                    <button key={p.id} onClick={() => { setProjectId(p.id); setPickOpen(false); setOpenFile(null); loadHistory(p.id); }} className="w-full text-left px-3 py-2 rounded-lg text-sm" style={{ color: p.id === projectId ? luxe.text : luxe.textMid, background: p.id === projectId ? luxe.surface2 : 'transparent' }}>
                      {p.name}
                    </button>
                  ))}
                  {projects.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: luxe.muted }}>No projects yet — just start chatting.</div>}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cred.balance !== null && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg" style={{ background: cred.low ? 'rgba(239,68,68,0.12)' : luxe.surface, border: `1px solid ${cred.low ? 'rgba(239,68,68,0.45)' : luxe.border}` }} data-testid="builder-credits">
              <Coins size={14} style={{ color: cred.low ? luxe.danger : luxe.gold }} />
              <span className="text-sm font-bold" style={{ color: cred.low ? '#FCA5A5' : luxe.text }}>{cred.balance}</span>
              <span className="text-[11px]" style={{ color: luxe.muted }}>{cred.percentage}%</span>
            </div>
          )}
          <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${luxe.border}` }}>
            {[['preview', 'Preview', Monitor], ['files', 'Files', FolderTree]].map(([id, label, Icon]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`builder-tab-${id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold" style={{ background: tab === id ? luxe.surface2 : 'transparent', color: tab === id ? luxe.text : luxe.muted }}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <button onClick={() => setManageOpen('connectors')} data-testid="builder-manage-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }}><SlidersHorizontal size={14} /> Manage</button>
          <button onClick={() => { setRefreshKey((k) => k + 1); if (projectId) loadFiles(projectId); }} title="Refresh preview" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><RefreshCw size={15} /></button>
          <a href={previewUrl} target="_blank" rel="noreferrer" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><ExternalLink size={15} /></a>
          <button onClick={share} data-testid="share-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }}><Share2 size={14} /> Share</button>
          <LuxeButton variant="blue" onClick={publish} data-testid="publish-btn" style={{ padding: '8px 16px' }}><UploadCloud size={15} className="inline mr-1" />Publish</LuxeButton>
        </div>
      </div>

      {/* Split */}
      <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
        {/* Left: chat */}
        <div className="flex flex-col min-h-0 lg:w-[44%]" style={{ borderRight: `1px solid ${luxe.border}` }}>
          <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="pt-6">
                <div className="flex items-center gap-2 mb-2"><div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: gradientCss(P.grad) }}><Bot size={18} color="#fff" /></div><div className="font-display text-xl font-black" style={{ color: luxe.text }}>Build anything, A → Z</div></div>
                <p className="text-sm mb-4" style={{ color: luxe.textMid }}>Describe the {mode === 'website' ? 'website' : 'app'} you want. Our AI agents plan, write the code and build it live — watch the preview on the right.</p>
                <div className="text-[11px] font-extrabold uppercase tracking-widest mb-2" style={{ color: luxe.muted }}>Your 16 AI Agents</div>
                <div className="flex flex-wrap gap-1.5" data-testid="agent-team">
                  {['🕵️ Error Detective','🐛 Bug Fixer','📊 Analytics Sage','👨‍💻 Coding Agent','🧪 Testing Agent','🎨 UI Designer','⚡ Freestyle Coder','🚀 Publisher','🧭 Orchestrator','📋 Product Manager','🏛️ System Architect','🏗️ Scaffolder','📝 Docs Writer','⚙️ DevOps','🔍 Code Reviewer','🛡️ Security Sentinel'].map((a) => (
                    <span key={a} className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>{a}</span>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i}>
                {m.role === 'user' ? (
                  <div className="flex justify-end"><div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-sm text-sm" style={{ background: gradientCss(P.grad), color: '#fff' }}>{m.text}{m.image_url && <img src={fileUrl(m.image_url)} alt="reference" className="mt-2 rounded-lg w-full max-w-[180px]" />}</div></div>
                ) : (
                  <div className="max-w-[92%]">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: gradientCss(P.grad) }}>{RUNNING.includes(m.status) ? <Loader2 size={13} color="#fff" className="animate-spin" /> : <Bot size={13} color="#fff" />}</div>
                      <span className="text-xs font-bold" style={{ color: luxe.text }}>{m.agent || 'MyRaai Agents'}</span>
                      {RUNNING.includes(m.status) && <span className="text-[10px]" style={{ color: luxe.muted }}>working…</span>}
                    </div>
                    <div className="px-3 py-2.5 rounded-2xl rounded-tl-sm text-sm" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>
                      <div style={{ color: luxe.text }}>{m.text}</div>
                      {(m.steps || []).length > 0 && (
                        <div className="mt-2 space-y-2">
                          {m.steps.map((s, j) => (
                            <div key={j} data-testid={`build-step-${j}`}><StepRow s={s} /></div>
                          ))}
                        </div>
                      )}
                      {m.final && <BuildReport final={m.final} steps={m.steps} />}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-3" style={{ borderTop: `1px solid ${luxe.border}`, background: luxe.bgElev }}>
            {clarify && (
              <div className="mb-3 rounded-xl overflow-hidden" data-testid="clarify-panel" style={{ border: `1px solid ${luxe.border}` }}>
                <div className="px-3 py-2 flex items-center gap-2" style={{ background: gradientCss(P.grad) }}>
                  <Sparkles size={14} color="#fff" />
                  <span className="text-xs font-bold" style={{ color: '#fff' }}>A few quick questions so I build exactly what you want</span>
                </div>
                <div className="p-3 space-y-3" style={{ background: luxe.surface, maxHeight: 280, overflowY: 'auto' }}>
                  {clarify.questions.map((q, i) => (
                    <div key={i} data-testid={`clarify-q-${i}`}>
                      <div className="text-sm font-semibold mb-1.5" style={{ color: luxe.text }}>{i + 1}. {q.q}</div>
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {q.options.map((opt, k) => {
                          const active = clarify.answers[i] === opt;
                          return (
                            <button key={k} data-testid={`clarify-opt-${i}-${k}`}
                              onClick={() => setClarify((c) => ({ ...c, answers: { ...c.answers, [i]: opt } }))}
                              className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
                              style={{ background: active ? gradientCss(P.grad) : luxe.surface2, color: active ? '#fff' : luxe.textMid, border: `1px solid ${active ? 'transparent' : luxe.border}` }}>
                              <span style={{ opacity: 0.7, marginRight: 4 }}>{String.fromCharCode(97 + k)}.</span>{opt}
                            </button>
                          );
                        })}
                      </div>
                      <input value={clarify.answers[i] && !q.options.includes(clarify.answers[i]) ? clarify.answers[i] : ''}
                        onChange={(e) => setClarify((c) => ({ ...c, answers: { ...c.answers, [i]: e.target.value } }))}
                        placeholder="…or type your own answer" data-testid={`clarify-input-${i}`}
                        className="w-full px-2.5 py-1.5 rounded-lg text-xs outline-none"
                        style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.text }} />
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={() => submitClarify(false)} data-testid="clarify-build-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: gradientCss(P.grad), color: '#fff' }}>Build it →</button>
                    <button onClick={() => submitClarify(true)} data-testid="clarify-skip-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>Skip, just build</button>
                  </div>
                </div>
              </div>
            )}
            {attach && (
              <div className="flex items-center gap-2 mb-2 p-2 rounded-xl w-fit" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
                <img src={fileUrl(attach.url)} alt="reference" className="w-9 h-9 rounded-lg object-cover" />
                <span className="text-xs" style={{ color: luxe.textMid }}>Reference image</span>
                <button onClick={() => setAttach(null)} data-testid="builder-remove-attach" style={{ color: luxe.danger }}><X size={14} /></button>
              </div>
            )}
            <div className="flex items-end gap-2 p-2 rounded-2xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="builder-file-input" />
              <button onClick={pickFile} disabled={uploading} data-testid="builder-attach-btn" className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }} title="Attach reference image">{uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={17} />}</button>
              <button onClick={toggleMic} data-testid="builder-mic-btn" className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: listening ? gradientCss(['#EC4899', '#A855F7']) : luxe.surface2, border: `1px solid ${luxe.border}`, color: listening ? '#fff' : luxe.textMid }} title="Voice input (speak your instruction)"><Mic size={17} /></button>
              <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1} placeholder={listening ? 'Listening… speak your instruction' : (mode === 'website' ? 'Describe the website to build…' : 'Describe the app to build…')} data-testid="builder-chat-input"
                className="flex-1 outline-none resize-none bg-transparent text-sm py-2 px-2" style={{ color: luxe.text, maxHeight: 120 }} />
              <button onClick={send} disabled={busy} data-testid="builder-send" className="w-10 h-10 rounded-xl flex items-center justify-center luxe-btn shrink-0" style={{ background: gradientCss(P.grad), opacity: busy ? 0.6 : 1 }}>
                {busy ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <Send size={18} color="#fff" />}
              </button>
            </div>
            {/* Composer toolbar: model selector · GitHub · Fork */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <div className="relative">
                <button onClick={() => setModelOpen(!modelOpen)} data-testid="builder-model-selector" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>
                  <Cpu size={13} /> {model ? model.label : 'Myra Recommended'} <ChevronDown size={12} />
                </button>
                {modelOpen && (
                  <div className="absolute z-40 bottom-full mb-1 w-72 p-2 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }} data-testid="model-menu">
                    <button onClick={() => { setModel(null); setModelOpen(false); }} data-testid="model-opt-recommended" className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold" style={{ color: !model ? luxe.text : luxe.textMid, background: !model ? luxe.surface2 : 'transparent' }}>
                      ✨ Myra Recommended <span style={{ color: luxe.muted }}>· uses the platform routing policy</span>
                    </button>
                    <div className="text-[10px] uppercase tracking-widest mt-2 mb-1 px-2" style={{ color: luxe.muted }}>Providers</div>
                    {providers.map((prov) => {
                      const std = (prov.tiers && (prov.tiers.standard || prov.tiers.simple)) || prov.id;
                      const configured = prov.status === 'configured';
                      return (
                        <button key={prov.id} disabled={!configured} onClick={() => { setModel({ provider: prov.id, model: std, label: `${prov.name} · ${std}` }); setModelOpen(false); }}
                          data-testid={`model-opt-${prov.id}`} className="w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between" style={{ color: configured ? luxe.text : luxe.muted, opacity: configured ? 1 : 0.6, background: model?.provider === prov.id ? luxe.surface2 : 'transparent' }}>
                          <span>{prov.name} <span style={{ color: luxe.muted }}>· {std}</span></span>
                          <span className="text-[10px] font-bold" style={{ color: configured ? luxe.success : luxe.muted }}>{configured ? 'Available' : 'Not configured'}</span>
                        </button>
                      );
                    })}
                    <div className="text-[10px] mt-1 px-2" style={{ color: luxe.muted }}>Only configured providers affect the build; others stay on the platform default.</div>
                  </div>
                )}
              </div>
              <button onClick={openChatGPT} data-testid="builder-chatgpt-btn" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }} title="Apni ChatGPT (OpenAI key) connect karein"><Sparkles size={13} /> Connect ChatGPT</button>
              <button onClick={openGithub} data-testid="builder-github-btn" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }} title="GitHub workspace"><Github size={13} /> GitHub</button>
              <button onClick={forkWorkspace} data-testid="builder-fork-btn" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }} title="Fork this project (secrets excluded)"><GitFork size={13} /> Fork</button>
            </div>
          </div>
        </div>

        {/* Right: preview / manage */}
        <div className="flex-1 min-h-0 flex flex-col" style={{ background: '#fff' }}>
          {tab === 'preview' ? (
            hasIndex ? (
              <iframe key={refreshKey} title="preview" src={previewUrl} data-testid="preview-iframe" className="flex-1 w-full border-0" style={{ background: '#fff' }} />
            ) : (
              <div className="flex-1 flex items-center justify-center" style={{ background: luxe.bg }}>
                <div className="text-center px-6">
                  <div className="mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: gradientCss(P.grad) }}><Monitor size={30} color="#fff" /></div>
                  <div className="font-bold text-lg" style={{ color: luxe.text }}>No preview yet</div>
                  <div className="text-sm mt-1 max-w-sm" style={{ color: luxe.muted }}>Ask the agent to build a website or web app (with an <b>index.html</b>) and it will appear here live.</div>
                </div>
              </div>
            )
          ) : (
            <div className="flex-1 min-h-0 flex" style={{ background: luxe.bg }}>
              <div className="w-56 overflow-y-auto p-3" style={{ borderRight: `1px solid ${luxe.border}` }}>
                <div className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: luxe.muted }}>Files ({files.length})</div>
                {files.length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>No files yet</div> : files.map((f) => (
                  <button key={f.path} onClick={() => openFileContent(f.path)} className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-mono truncate" style={{ color: openFile?.path === f.path ? luxe.text : luxe.textMid, background: openFile?.path === f.path ? luxe.surface2 : 'transparent' }}>{f.path}</button>
                ))}
              </div>
              <div className="flex-1 overflow-auto p-4">
                {openFile ? (
                  <div className="h-full flex flex-col" data-testid="ide-editor">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono" style={{ color: luxe.text }}>{openFile.path}{openFile.buffer !== openFile.content ? ' •' : ''}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setOpenFile({ ...openFile, buffer: openFile.content })} disabled={openFile.buffer === openFile.content} data-testid="ide-revert" className="px-2 py-1 rounded text-xs font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid, opacity: openFile.buffer === openFile.content ? 0.5 : 1 }}>Revert</button>
                        <button onClick={saveFile} disabled={openFile.buffer === openFile.content} data-testid="ide-save" className="px-2 py-1 rounded text-xs font-bold" style={{ background: gradientCss(P.grad), color: '#fff', opacity: openFile.buffer === openFile.content ? 0.5 : 1 }}>Save</button>
                      </div>
                    </div>
                    <textarea value={openFile.buffer} onChange={(e) => setOpenFile({ ...openFile, buffer: e.target.value })} spellCheck={false} data-testid="ide-textarea"
                      className="flex-1 w-full outline-none resize-none font-mono text-xs rounded-lg p-3" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text, minHeight: 320, lineHeight: 1.6, tabSize: 2 }} />
                  </div>
                ) : <Empty icon={FileCode2} title="Select a file" desc="Click a file on the left to view and edit its code." />}
              </div>
            </div>
          )}
        </div>
      </div>

      {cgpt?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setCgpt(null)} data-testid="chatgpt-modal">
          <div className="w-full max-w-md p-6 rounded-2xl" style={{ background: luxe.bgElev, border: `1px solid ${luxe.border}` }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2"><Sparkles size={20} style={{ color: luxe.blue }} /><span className="font-display text-xl font-black" style={{ color: luxe.text }}>Connect ChatGPT</span></div>
              <button onClick={() => setCgpt(null)} data-testid="chatgpt-close" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><X size={16} /></button>
            </div>
            {cgpt.loading ? <div className="flex gap-2" style={{ color: luxe.muted }}><Loader2 size={15} className="animate-spin" /> Loading…</div>
              : cgpt.connected ? (
                <div data-testid="chatgpt-connected">
                  <div className="p-2.5 rounded-lg mb-3 flex items-center justify-between" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
                    <span className="text-sm" style={{ color: luxe.text }}>✅ ChatGPT connected — builds use YOUR key</span>
                    <button onClick={cgptDisconnect} data-testid="chatgpt-disconnect" className="text-xs" style={{ color: '#FCA5A5' }}>Disconnect</button>
                  </div>
                  <div className="text-[11px]" style={{ color: luxe.muted }}>Aapki OpenAI key encrypted store hoti hai, kabhi dikhayi nahi jaati. Agar key fail ho to build apne-aap platform key par chal jaata hai (kaam nahi rukta).</div>
                </div>
              ) : (
                <div data-testid="chatgpt-connect">
                  <div className="text-sm mb-3" style={{ color: luxe.muted }}>Apni ChatGPT (OpenAI) API key connect karein — phir aapki saari coding aapki hi ChatGPT se chalegi, aur wahi files dekh kar agle steps decide karegi. Key <b>platform.openai.com</b> se banayein (API access + billing ke saath).</div>
                  <Input type="password" placeholder="sk-… (OpenAI API key)" value={cgptKey} onChange={(e) => setCgptKey(e.target.value)} data-testid="chatgpt-key" style={{ marginBottom: 8 }} />
                  <LuxeButton full onClick={cgptConnect} data-testid="chatgpt-connect-btn"><Sparkles size={15} className="inline mr-1" />Connect ChatGPT</LuxeButton>
                  <div className="text-[11px] mt-2" style={{ color: luxe.muted }}>Key encrypted rehti hai aur kabhi return/log nahi hoti. Disconnect kabhi bhi kar sakte hain.</div>
                </div>
              )}
          </div>
        </div>
      )}

      {gh && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="github-drawer">
          <div className="flex-1" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setGh(null)} />
          <div className="w-full max-w-md h-full overflow-y-auto p-6" style={{ background: luxe.bgElev, borderLeft: `1px solid ${luxe.border}` }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2"><Github size={20} style={{ color: luxe.text }} /><span className="font-display text-xl font-black" style={{ color: luxe.text }}>GitHub</span></div>
              <button onClick={() => setGh(null)} data-testid="github-close" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><X size={16} /></button>
            </div>
            {gh.loading ? <div className="flex gap-2" style={{ color: luxe.muted }}><Loader2 size={15} className="animate-spin" /> Loading…</div>
              : !gh.status?.connected ? (
                <div data-testid="github-connect">
                  <div className="text-sm mb-3" style={{ color: luxe.muted }}>Connect your GitHub account to push this project to your own repository. Your token is encrypted and never shown again.</div>
                  <Input placeholder="GitHub username" value={ghToken.username} onChange={(e) => setGhToken({ ...ghToken, username: e.target.value })} data-testid="github-username" style={{ marginBottom: 8 }} />
                  <Input type="password" placeholder="Personal access token" value={ghToken.token} onChange={(e) => setGhToken({ ...ghToken, token: e.target.value })} data-testid="github-token" style={{ marginBottom: 8 }} />
                  <LuxeButton full onClick={ghConnect} data-testid="github-connect-btn"><Github size={15} className="inline mr-1" />Connect GitHub</LuxeButton>
                  <div className="text-[11px] mt-2" style={{ color: luxe.muted }}>Prefer OAuth/connector setup? Open <button onClick={() => { setGh(null); setManageOpen('connectors'); }} className="underline" style={{ color: luxe.blue }}>Manage → Connectors</button>.</div>
                </div>
              ) : (
                <div data-testid="github-workspace">
                  <div className="p-2.5 rounded-lg mb-3 flex items-center justify-between" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
                    <span className="text-sm" style={{ color: luxe.text }}>Connected as <b>{gh.status.account}</b></span>
                    <button onClick={ghDisconnect} data-testid="github-disconnect" className="text-xs" style={{ color: '#FCA5A5' }}>Disconnect</button>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>Repository</div>
                  <Input placeholder="client-org/my-project" value={gh.repo} onChange={(e) => setGh({ ...gh, repo: e.target.value })} data-testid="github-repo" style={{ marginBottom: 8 }} />
                  <Input placeholder="branch (main)" value={gh.branch} onChange={(e) => setGh({ ...gh, branch: e.target.value })} data-testid="github-branch" style={{ marginBottom: 8 }} />
                  <button onClick={ghReview} data-testid="github-review" className="w-full px-3 py-2 rounded-lg text-sm font-bold mb-2" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }}>Review Changes</button>
                  {gh.changes && (
                    <div className="p-2.5 rounded-lg mb-2 text-xs" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }} data-testid="github-changes">
                      {(gh.changes.files || gh.changes.changed || []).length || gh.changes.count || 0} changed file(s){gh.changes.summary ? ` · ${gh.changes.summary}` : ''}
                    </div>
                  )}
                  <textarea rows={2} placeholder="Commit message" value={gh.message} onChange={(e) => setGh({ ...gh, message: e.target.value })} data-testid="github-message" className="w-full outline-none resize-none rounded-lg p-2 text-sm mb-2" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }} />
                  <LuxeButton full onClick={ghPush} data-testid="github-push"><Github size={15} className="inline mr-1" />Push to GitHub</LuxeButton>
                  <div className="text-[11px] mt-2" style={{ color: luxe.muted }}>Nothing is pushed until you click Push. Pushes go only to your authorized repository.</div>
                </div>
              )}
          </div>
        </div>
      )}
      {manageOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="workspace-manage-drawer">
          <div className="flex-1" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }} onClick={() => setManageOpen(false)} />
          <div className="w-full max-w-2xl h-full overflow-y-auto p-6" style={{ background: luxe.bgElev, borderLeft: `1px solid ${luxe.border}` }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-2xl font-black" style={{ color: luxe.text }}>Manage</div>
                <div className="text-xs" style={{ color: luxe.muted }}>{currentProject?.name || 'Workspace'} · connectors, payments, secrets & AI providers</div>
              </div>
              <button onClick={() => setManageOpen(false)} data-testid="manage-drawer-close" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><X size={16} /></button>
            </div>
            <ManageTabs initial={typeof manageOpen === 'string' ? manageOpen : 'connectors'} project={projectId} />
          </div>
        </div>
      )}
    </div>
  );
}
